import React, {
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    Animated,
    Keyboard,
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    ScrollView as RNScrollView,
    View,
} from 'react-native';
import { YStack } from 'smoothie';

import { ChatColumn } from './ChatColumn';
import { useMobileWebChatContainerBottom } from './MobileWebChatLayoutContext';

interface ChatMessageLayoutProps {
    children: ReactNode;
    footer: ReactNode;
    scrollViewRef?: RefObject<RNScrollView | null>;
    onContentSizeChange?: (contentWidth: number, contentHeight: number) => void;
    onScrollViewLayout?: (height: number) => void;
    onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onFooterLayout?: (height: number) => void;
    /** Fires when the portaled footer moves above the mobile-web keyboard. */
    onMobileWebKeyboardFooterBottomChange?: (bottom: number) => void;
    /** When true, messages stay put and the keyboard overlaps them; only the footer moves above the keyboard. */
    keyboardOverlapsContent?: boolean;
    /** Slice of the footer (e.g. just the disclosure line) that content must never render behind. */
    reservedFooterHeight?: number;
}

/**
 * Clearance kept between the footer and the top of the keyboard. This is ADDED
 * to the offset so the footer (and its interactive controls, e.g. the submit
 * button) sit fully above the keyboard. Subtracting here would push the bottom
 * of the footer behind the keyboard, where iOS swallows taps to dismiss it.
 */
const MOBILE_WEB_KEYBOARD_FOOTER_GAP = 8;

const SCROLL_BOTTOM_THRESHOLD = 24;

const MOBILE_WEB_FOOTER_TRANSFORM_TRANSITION =
    'bottom 250ms cubic-bezier(0.32, 0.72, 0, 1)';

const MOBILE_WEB_KEYBOARD_OPEN_THRESHOLD = 0.92;

const getMobileWebFooterOffset = (containerBottom: number): number => {
    if (typeof window === 'undefined') {
        return 0;
    }

    const visualViewport = window.visualViewport;
    if (visualViewport == null) {
        return 0;
    }

    if (
        visualViewport.height >=
        containerBottom * MOBILE_WEB_KEYBOARD_OPEN_THRESHOLD
    ) {
        return 0;
    }

    const rawOffset = containerBottom - visualViewport.height;

    return Math.max(0, rawOffset + MOBILE_WEB_KEYBOARD_FOOTER_GAP);
};

const isFocusableInput = (target: EventTarget | null): boolean => {
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
    );
};

const applyFooterViewportOffset = (
    footerRef: RefObject<View | null>,
    offsetTop: number,
): void => {
    const node = footerRef.current as unknown as HTMLElement | null;
    if (node?.style == null) {
        return;
    }

    node.style.transform =
        offsetTop > 0 ? `translate3d(0, ${offsetTop.toString()}px, 0)` : '';
};

const applyMobileWebFooterTransition = (
    footerRef: RefObject<View | null>,
): void => {
    const node = footerRef.current as unknown as HTMLElement | null;
    if (node?.style == null) {
        return;
    }

    node.style.transition = MOBILE_WEB_FOOTER_TRANSFORM_TRANSITION;
};

export const ChatMessageLayout = ({
    children,
    footer,
    scrollViewRef,
    onContentSizeChange,
    onScrollViewLayout,
    onScroll,
    onFooterLayout,
    onMobileWebKeyboardFooterBottomChange,
    keyboardOverlapsContent = false,
    reservedFooterHeight = 0,
}: ChatMessageLayoutProps) => {
    const keyboardHeight = useRef(new Animated.Value(0)).current;
    const footerRef = useRef<View>(null);
    const mobileWebContainerBottom = useMobileWebChatContainerBottom();
    const mobileWebContainerBottomRef = useRef(mobileWebContainerBottom);
    const footerOffsetRef = useRef(0);
    const footerViewportOffsetTopRef = useRef(0);
    const scrollOffsetRef = useRef(0);
    const scrollContentHeightRef = useRef(0);
    const scrollViewHeightRef = useRef(0);
    const followBottomOnKeyboardRef = useRef(false);
    const scrollLockUntilRef = useRef(0);
    const [mobileWebKeyboardFooterBottom, setMobileWebKeyboardFooterBottom] =
        useState(0);
    const [footerOffset, setFooterOffset] = useState(0);
    const [lockedContainerHeight, setLockedContainerHeight] = useState<
        number | null
    >(null);
    const hasLockedContainerHeightRef = useRef(false);

    mobileWebContainerBottomRef.current = mobileWebContainerBottom;

    const readScrollMetricsFromDom = useCallback(() => {
        const node = scrollViewRef?.current as unknown as HTMLElement | null;
        if (node == null || !('scrollTop' in node)) {
            return;
        }

        scrollOffsetRef.current = node.scrollTop;
        scrollContentHeightRef.current = node.scrollHeight;
        scrollViewHeightRef.current = node.clientHeight;
    }, [scrollViewRef]);

    const isScrolledToBottom = useCallback((): boolean => {
        readScrollMetricsFromDom();
        const maxScroll = Math.max(
            0,
            scrollContentHeightRef.current - scrollViewHeightRef.current,
        );

        return scrollOffsetRef.current >= maxScroll - SCROLL_BOTTOM_THRESHOLD;
    }, [readScrollMetricsFromDom]);

    const scrollToBottom = useCallback(
        (animated: boolean) => {
            scrollViewRef?.current?.scrollToEnd({ animated });

            const node =
                scrollViewRef?.current as unknown as HTMLElement | null;
            if (node != null && 'scrollTop' in node) {
                node.scrollTop = node.scrollHeight;
                scrollOffsetRef.current = node.scrollTop;
                scrollContentHeightRef.current = node.scrollHeight;
                scrollViewHeightRef.current = node.clientHeight;
            }
        },
        [scrollViewRef],
    );

    const lockScrollPosition = useCallback(() => {
        const lockedOffset = scrollOffsetRef.current;
        scrollViewRef?.current?.scrollTo({
            y: lockedOffset,
            animated: false,
        });

        const node = scrollViewRef?.current as unknown as HTMLElement | null;
        if (node != null && 'scrollTop' in node) {
            node.scrollTop = lockedOffset;
        }
    }, [scrollViewRef]);

    const beginScrollLock = useCallback(() => {
        scrollLockUntilRef.current = Date.now() + 500;

        let frameCount = 0;
        const tick = () => {
            lockScrollPosition();
            frameCount += 1;
            if (frameCount < 12) {
                requestAnimationFrame(tick);
            }
        };
        tick();
    }, [lockScrollPosition]);

    const handleChatInputFocus = useCallback(() => {
        if (isScrolledToBottom()) {
            followBottomOnKeyboardRef.current = true;
            scrollLockUntilRef.current = 0;
            scrollToBottom(false);
            return;
        }

        followBottomOnKeyboardRef.current = false;
        beginScrollLock();
    }, [beginScrollLock, isScrolledToBottom, scrollToBottom]);

    const updateMobileWebFooterOffset = useCallback(() => {
        const containerBottom = mobileWebContainerBottomRef.current;
        if (containerBottom == null) {
            return;
        }

        const wasOpen = footerOffsetRef.current > 0;
        const offset = getMobileWebFooterOffset(containerBottom);
        footerOffsetRef.current = offset;
        if (offset <= 0) {
            followBottomOnKeyboardRef.current = false;
        } else if (!wasOpen && isScrolledToBottom()) {
            followBottomOnKeyboardRef.current = true;
        }
        setMobileWebKeyboardFooterBottom(offset);
        onMobileWebKeyboardFooterBottomChange?.(offset);
    }, [isScrolledToBottom, onMobileWebKeyboardFooterBottomChange]);

    const syncFooterViewportCompensation = useCallback(() => {
        if (footerOffsetRef.current <= 0) {
            footerViewportOffsetTopRef.current = 0;
            applyFooterViewportOffset(footerRef, 0);
            return;
        }

        const offsetTop = window.visualViewport?.offsetTop ?? 0;
        footerViewportOffsetTopRef.current = offsetTop;
        applyFooterViewportOffset(footerRef, offsetTop);
    }, []);

    useLayoutEffect(() => {
        if (!keyboardOverlapsContent || Platform.OS !== 'web') {
            return;
        }

        applyFooterViewportOffset(
            footerRef,
            footerViewportOffsetTopRef.current,
        );
        applyMobileWebFooterTransition(footerRef);
    });

    useLayoutEffect(() => {
        if (
            !keyboardOverlapsContent ||
            Platform.OS !== 'web' ||
            !followBottomOnKeyboardRef.current ||
            mobileWebKeyboardFooterBottom <= 0
        ) {
            return;
        }

        scrollToBottom(false);
    }, [
        keyboardOverlapsContent,
        mobileWebKeyboardFooterBottom,
        scrollToBottom,
    ]);

    useLayoutEffect(() => {
        if (
            !keyboardOverlapsContent ||
            Platform.OS !== 'web' ||
            mobileWebContainerBottom == null ||
            typeof window === 'undefined'
        ) {
            return undefined;
        }

        const visualViewport = window.visualViewport;
        if (visualViewport == null) {
            return undefined;
        }

        const handleVisualViewportChange = () => {
            updateMobileWebFooterOffset();
            if (followBottomOnKeyboardRef.current) {
                scrollToBottom(false);
            } else if (Date.now() < scrollLockUntilRef.current) {
                lockScrollPosition();
            }
            syncFooterViewportCompensation();
        };

        const handleTouchStart = (event: TouchEvent) => {
            if (isFocusableInput(event.target)) {
                handleChatInputFocus();
                return;
            }

            if (footerOffsetRef.current > 0) {
                syncFooterViewportCompensation();
            }
        };

        const handleFocusIn = (event: FocusEvent) => {
            if (isFocusableInput(event.target)) {
                handleChatInputFocus();
            }
        };

        visualViewport.addEventListener('resize', handleVisualViewportChange);
        visualViewport.addEventListener('scroll', handleVisualViewportChange);
        document.addEventListener('touchstart', handleTouchStart, true);
        document.addEventListener('focusin', handleFocusIn, true);
        updateMobileWebFooterOffset();
        syncFooterViewportCompensation();

        return () => {
            visualViewport.removeEventListener(
                'resize',
                handleVisualViewportChange,
            );
            visualViewport.removeEventListener(
                'scroll',
                handleVisualViewportChange,
            );
            document.removeEventListener('touchstart', handleTouchStart, true);
            document.removeEventListener('focusin', handleFocusIn, true);
            footerOffsetRef.current = 0;
            footerViewportOffsetTopRef.current = 0;
            followBottomOnKeyboardRef.current = false;
            applyFooterViewportOffset(footerRef, 0);
            setMobileWebKeyboardFooterBottom(0);
            onMobileWebKeyboardFooterBottomChange?.(0);
        };
    }, [
        handleChatInputFocus,
        keyboardOverlapsContent,
        lockScrollPosition,
        mobileWebContainerBottom,
        onMobileWebKeyboardFooterBottomChange,
        scrollToBottom,
        syncFooterViewportCompensation,
        updateMobileWebFooterOffset,
    ]);

    useLayoutEffect(() => {
        if (
            !keyboardOverlapsContent ||
            Platform.OS !== 'web' ||
            mobileWebKeyboardFooterBottom <= 0
        ) {
            return undefined;
        }

        const scrollNode =
            scrollViewRef?.current as unknown as HTMLElement | null;
        if (scrollNode?.style == null) {
            return undefined;
        }

        scrollNode.style.overscrollBehavior = 'contain';
        scrollNode.style.touchAction = 'pan-y';

        return () => {
            scrollNode.style.overscrollBehavior = '';
            scrollNode.style.touchAction = '';
        };
    }, [keyboardOverlapsContent, mobileWebKeyboardFooterBottom, scrollViewRef]);

    useEffect(() => {
        if (!keyboardOverlapsContent || Platform.OS === 'web') {
            return undefined;
        }

        const showEvent =
            Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
        const hideEvent =
            Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

        const show = Keyboard.addListener(showEvent, (event) => {
            setFooterOffset(event.endCoordinates.height);
        });
        const hide = Keyboard.addListener(hideEvent, () => {
            setFooterOffset(0);
        });

        return () => {
            show.remove();
            hide.remove();
        };
    }, [keyboardOverlapsContent]);

    useEffect(() => {
        if (keyboardOverlapsContent) {
            return undefined;
        }

        const showEvent =
            Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
        const hideEvent =
            Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

        const show = Keyboard.addListener(showEvent, (event) => {
            Animated.timing(keyboardHeight, {
                toValue: event.endCoordinates.height,
                duration: event.duration || 250,
                useNativeDriver: false,
            }).start();
            setTimeout(
                () => scrollViewRef?.current?.scrollToEnd({ animated: true }),
                (event.duration || 250) + 50,
            );
        });

        const hide = Keyboard.addListener(hideEvent, (event) => {
            Animated.timing(keyboardHeight, {
                toValue: 0,
                duration: event.duration || 250,
                useNativeDriver: false,
            }).start();
        });

        return () => {
            show.remove();
            hide.remove();
        };
    }, [keyboardHeight, keyboardOverlapsContent, scrollViewRef]);

    const handleContainerLayout = (event: LayoutChangeEvent) => {
        if (
            !keyboardOverlapsContent ||
            Platform.OS === 'web' ||
            hasLockedContainerHeightRef.current ||
            event.nativeEvent.layout.height <= 0
        ) {
            return;
        }

        hasLockedContainerHeightRef.current = true;
        setLockedContainerHeight(event.nativeEvent.layout.height);
    };

    const handleScrollContentSizeChange = useCallback(
        (width: number, height: number) => {
            scrollContentHeightRef.current = height;

            if (followBottomOnKeyboardRef.current) {
                scrollToBottom(false);
            }

            onContentSizeChange?.(width, height);
        },
        [onContentSizeChange, scrollToBottom],
    );

    const scrollView = (
        <RNScrollView
            ref={scrollViewRef}
            style={
                keyboardOverlapsContent && reservedFooterHeight > 0
                    ? {
                          flex: 1,
                          minHeight: 0,
                          // Margin, not padding — actually shrinks the viewport.
                          marginBottom: reservedFooterHeight,
                      }
                    : { flex: 1, minHeight: 0 }
            }
            contentContainerStyle={
                keyboardOverlapsContent ? undefined : { flexGrow: 1 }
            }
            keyboardShouldPersistTaps="handled"
            automaticallyAdjustKeyboardInsets={false}
            automaticallyAdjustContentInsets={false}
            scrollEventThrottle={16}
            onScroll={
                keyboardOverlapsContent
                    ? (event: NativeSyntheticEvent<NativeScrollEvent>) => {
                          if (Date.now() < scrollLockUntilRef.current) {
                              return;
                          }

                          const {
                              contentOffset,
                              contentSize,
                              layoutMeasurement,
                          } = event.nativeEvent;
                          scrollOffsetRef.current = contentOffset.y;
                          scrollContentHeightRef.current = contentSize.height;
                          scrollViewHeightRef.current =
                              layoutMeasurement.height;

                          if (followBottomOnKeyboardRef.current) {
                              const maxScroll = Math.max(
                                  0,
                                  contentSize.height - layoutMeasurement.height,
                              );
                              if (
                                  contentOffset.y <
                                  maxScroll - SCROLL_BOTTOM_THRESHOLD
                              ) {
                                  followBottomOnKeyboardRef.current = false;
                              }
                          }

                          onScroll?.(event);
                      }
                    : undefined
            }
            onContentSizeChange={handleScrollContentSizeChange}
            onLayout={(layoutEvent) => {
                if (keyboardOverlapsContent) {
                    scrollViewHeightRef.current =
                        layoutEvent.nativeEvent.layout.height;
                }

                onScrollViewLayout?.(layoutEvent.nativeEvent.layout.height);
            }}
        >
            <YStack width="100%" alignItems="center">
                <ChatColumn width="100%">{children}</ChatColumn>
            </YStack>
        </RNScrollView>
    );

    const footerContent = (
        <YStack width="100%" alignItems="center" flexShrink={0}>
            <ChatColumn width="100%">{footer}</ChatColumn>
        </YStack>
    );

    const isMobileWebOverlapFooter =
        Platform.OS === 'web' && keyboardOverlapsContent;

    if (keyboardOverlapsContent) {
        const footerElement = (
            <View
                ref={footerRef}
                onLayout={
                    onFooterLayout != null
                        ? (event) => {
                              onFooterLayout(event.nativeEvent.layout.height);
                          }
                        : undefined
                }
                style={
                    isMobileWebOverlapFooter
                        ? {
                              position: 'fixed',
                              left: 0,
                              right: 0,
                              bottom: mobileWebKeyboardFooterBottom,
                              zIndex: 20,
                              width: '100%',
                          }
                        : {
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              transform: [
                                  { translateY: -footerOffset },
                              ] as const,
                          }
                }
            >
                {footerContent}
            </View>
        );

        return (
            <View
                onLayout={handleContainerLayout}
                style={{
                    flex: 1,
                    minHeight: 0,
                    position: 'relative',
                    ...(lockedContainerHeight != null
                        ? {
                              height: lockedContainerHeight,
                              maxHeight: lockedContainerHeight,
                          }
                        : {}),
                }}
            >
                {scrollView}
                {isMobileWebOverlapFooter && typeof document !== 'undefined'
                    ? createPortal(footerElement, document.body)
                    : footerElement}
            </View>
        );
    }

    return (
        <Animated.View
            style={{ flex: 1, minHeight: 0, paddingBottom: keyboardHeight }}
        >
            <YStack flex={1} minHeight={0} overflow="hidden">
                {scrollView}
                {footerContent}
            </YStack>
        </Animated.View>
    );
};
